import React, { useCallback, useEffect, useState } from 'react';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';
import TextInput from '@common/components/Inputs/TextInput';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { isHttpError } from '@common/interfaces/api/http-error';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { CredentialResponse } from '@react-oauth/google';
import { useAppDispatch, useAppSelector } from 'store';
import { disableTwoFactor, getUserByToken } from 'store/general/general-actions';
import { resetTwoFactorRestState } from 'store/general/general-slice';
import { selectTwoFactorRest, selectUser } from 'store/general/general-slice-selectors';
import LoginVariant from 'models/enums/login-variant';
import GoogleLoginInput from 'components/forms/GoogleLoginInput';
import { FormHelperText } from '@mui/material';

interface DisableTwoFactorDialogProps {
  open: boolean;
  onClose: () => void;
}

const DisableTwoFactorDialog: React.FC<DisableTwoFactorDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const twoFactorRest = useAppSelector((state) => selectTwoFactorRest(state));
  const currentUser = useAppSelector((state) => selectUser(state));

  const [currentPassword, setCurrentPassword] = useState('');
  const [googleToken, setGoogleToken] = useState('');
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  const isGoogleLogin = currentUser.data?.lastLoginVariant === LoginVariant.Google;

  const resetAndClose = useCallback(() => {
    setCurrentPassword('');
    setGoogleToken('');
    setCode('');
    setUseBackupCode(false);
    onClose();
    dispatch(
      resetTwoFactorRestState({
        disableTwoFactorPostError: null,
        disableTwoFactorPostStatus: HttpFetchStatus.Idle,
      }),
    );
  }, [dispatch, onClose]);

  const googleLoginSuccessHandler = (response: CredentialResponse) => {
    if (response.credential != null) {
      setGoogleToken(response.credential);
    }
  };

  useEffect(() => {
    if (twoFactorRest.disableTwoFactorPostStatus === HttpFetchStatus.Success) {
      resetAndClose();
      dispatch(getUserByToken());
    }
  }, [dispatch, resetAndClose, twoFactorRest.disableTwoFactorPostStatus]);

  const submitHandler = () => {
    dispatch(
      disableTwoFactor({
        currentPassword: isGoogleLogin ? undefined : currentPassword,
        googleToken: isGoogleLogin ? googleToken : undefined,
        otp: useBackupCode ? undefined : code,
        backupCode: useBackupCode ? code : undefined,
      }),
    );
  };

  const toggleBackupCodeHandler = () => {
    setUseBackupCode((prev) => !prev);
    setCode('');
    dispatch(
      resetTwoFactorRestState({
        disableTwoFactorPostError: null,
        disableTwoFactorPostStatus: HttpFetchStatus.Idle,
      }),
    );
  };

  const resetSuccessSnackbarHandler = () => {
    dispatch(
      resetTwoFactorRestState({
        disableTwoFactorPostError: null,
        disableTwoFactorPostStatus: HttpFetchStatus.Idle,
      }),
    );
  };

  return (
    <React.Fragment>
      <DialogWrapperWithClose
        title={t('title-two-factor-disable')}
        open={open}
        onClose={resetAndClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <FormHelperText>
              {isGoogleLogin
                ? t('form-help-text-disable-2fa-3rd-party')
                : t('form-help-text-disable-2fa')}
            </FormHelperText>
            {isGoogleLogin ? (
              <GoogleLoginInput
                onSuccess={googleLoginSuccessHandler}
                onError={() => setGoogleToken('')}
              />
            ) : (
              <TextInput
                type="password"
                label={t('input-label-current-password')}
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            )}
            <TextInput
              label={
                useBackupCode ? t('input-label-backup-code') : t('input-label-authenticator-code')
              }
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            <Link
              component="button"
              onClick={toggleBackupCodeHandler}
              sx={{ alignSelf: 'flex-start' }}
            >
              {useBackupCode
                ? t('link-use-authenticator-code-instead')
                : t('link-use-backup-code-instead')}
            </Link>
            {twoFactorRest.disableTwoFactorPostError != null &&
              isHttpError(twoFactorRest.disableTwoFactorPostError) && (
                <CustomAlert
                  header={t('error-two-factor-disable-failed')}
                  textMessages={[t(twoFactorRest.disableTwoFactorPostError.message)]}
                  severity="error"
                />
              )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAndClose} variant="text">
            {t('button-cancel')}
          </Button>
          <Button
            onClick={submitHandler}
            variant="contained"
            disabled={
              (isGoogleLogin ? googleToken.length === 0 : currentPassword.length === 0) ||
              code.length === 0 ||
              twoFactorRest.disableTwoFactorPostStatus === HttpFetchStatus.Loading
            }
          >
            {t('button-disable-2fa')}
          </Button>
        </DialogActions>
      </DialogWrapperWithClose>
      <CustomSnackbar
        open={twoFactorRest.disableTwoFactorPostStatus === HttpFetchStatus.Success}
        onClose={resetSuccessSnackbarHandler}
        title={t('success-disabling-2fa')}
      />
    </React.Fragment>
  );
};

export default DisableTwoFactorDialog;
